import { useContext, useEffect, useMemo, useRef } from "react";
import { gql } from "@apollo/client";
import { debounce } from "lodash";

import { SetActiveClientInput } from "@src/types.generated";

import { ClientIdContext } from "./ClientIdContext";
import {
  useRemoveActiveClientMutation,
  useSetActiveClientMutation,
} from "./useSyncActiveClientCore.generated";

/**
 * Registers the current client (browser tab) as actively editing
 * whatever content the caller describes in `input`. Refreshes every
 * 10s to keep the server-side Redis TTL alive (entries expire after
 * 30s). Cleans up on unmount and on `beforeunload`.
 *
 * Pass `null` to opt out (e.g. when the editing context isn't ready
 * yet, or the caller wants to disable presence on a specific route).
 *
 * Each domain (LibraryItem / workflow) owns its own input-assembly
 * wrapper since the context shapes differ; this core owns the
 * orchestration.
 */
const useSyncActiveClientCore = (input: SetActiveClientInput | null): void => {
  const [setActiveClientMutation] = useSetActiveClientMutation({
    ignoreResults: true,
  });
  const [removeActiveClientMutation] = useRemoveActiveClientMutation();

  // Leading edge registers immediately on mount (no avatar gap when
  // navigating between builders); bursts still coalesce onto the
  // trailing edge. Input is read via ref so the instance stays stable
  // across input changes.
  const inputRef = useRef(input);
  inputRef.current = input;
  const debouncedSetActiveClient = useMemo(
    () =>
      debounce(
        () => {
          const current = inputRef.current;
          if (!current) return;
          setActiveClientMutation({ variables: { input: current } });
        },
        1000,
        { leading: true, trailing: true },
      ),
    [setActiveClientMutation],
  );

  useEffect(() => {
    debouncedSetActiveClient();
  }, [debouncedSetActiveClient, input]);

  useEffect(() => {
    const interval = setInterval(() => debouncedSetActiveClient(), 1000 * 10);
    return () => {
      clearInterval(interval);
      debouncedSetActiveClient.cancel();
    };
  }, [debouncedSetActiveClient]);

  // Removal is keyed on the session clientId (not `input`) so this
  // effect registers exactly once and fires only on true unmount /
  // beforeunload. Keying it on `input` would tear the presence entry
  // down on every screen / step switch (avatar flicker on other
  // clients), and gating on a non-null `input` would strand the
  // entry for the Redis TTL when the caller opts out (`input: null`)
  // after having registered.
  const clientId = useContext(ClientIdContext)?.clientId;
  useEffect(() => {
    if (!clientId) return;
    const unload = (): void => {
      removeActiveClientMutation({ variables: { clientId } });
    };
    window.addEventListener("beforeunload", unload);
    return () => {
      unload();
      window.removeEventListener("beforeunload", unload);
    };
  }, [clientId, removeActiveClientMutation]);
};

export default useSyncActiveClientCore;

gql`
  mutation SetActiveClient($input: SetActiveClientInput!) {
    setActiveClient(input: $input) {
      success
    }
  }

  mutation RemoveActiveClient($clientId: String!) {
    removeActiveClient(clientId: $clientId) {
      success
    }
  }
`;
